import React, { useState, useEffect, useCallback } from "react";
import {
  MessageSquare, AlertTriangle, Loader2, Lightbulb, Check, X, ShieldAlert, Filter, Mail, User, Clock, Eye
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only Team Hub card: Employee Voice
 * Lists every suggestion + concern submitted via the internal DoozyBot widget.
 * Actions:
 *   - Suggestions → Mark Implemented (auto-thanks employee via email+WA) / Under Review / Decline
 *   - Concerns    → Mark Under Review / Resolved / Dismissed
 * Accountability: "opted anonymous" chip shown with the employee's real name
 * so super admin always has traceability.
 */
const EmployeeVoiceCard = ({ authAxios }) => {
  const [tab, setTab] = useState("suggestions"); // suggestions | concerns
  const [statusFilter, setStatusFilter] = useState("all");
  const [suggestions, setSuggestions] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // selected row for modal

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        authAxios.get(`${API_URL}/api/admin/doozybot/suggestions`),
        authAxios.get(`${API_URL}/api/admin/doozybot/concerns`),
      ]);
      setSuggestions(s.data.items || []);
      setConcerns(c.data.items || []);
    } catch {}
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = (tab === "suggestions" ? suggestions : concerns).filter(
    (x) => statusFilter === "all" || x.status === statusFilter
  );

  const statusOptions = tab === "suggestions"
    ? ["all", "new", "under_review", "implemented", "declined"]
    : ["all", "new", "under_review", "resolved", "dismissed"];

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="employee-voice-card">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#E83E8C] flex items-center justify-center">
            <MessageSquare size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-bold text-[#1E1B4B] text-sm">Employee Voice</h3>
            <p className="text-xs text-[#94a3b8]">
              Suggestions & concerns raised via internal DoozyBot · {suggestions.length} ideas · {concerns.length} concerns
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-50 p-1 rounded-xl mb-3">
        <button
          onClick={() => { setTab("suggestions"); setStatusFilter("all"); }}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition ${
            tab === "suggestions" ? "bg-white text-[#E83E8C] shadow" : "text-gray-500"
          }`}
          data-testid="ev-tab-suggestions"
        >
          <Lightbulb size={13} /> Suggestions ({suggestions.length})
        </button>
        <button
          onClick={() => { setTab("concerns"); setStatusFilter("all"); }}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition ${
            tab === "concerns" ? "bg-white text-[#DC283C] shadow" : "text-gray-500"
          }`}
          data-testid="ev-tab-concerns"
        >
          <AlertTriangle size={13} /> Concerns ({concerns.length})
        </button>
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        <Filter size={11} className="text-gray-400" />
        {statusOptions.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition ${
              statusFilter === s
                ? "border-[#E83E8C] bg-[#FFF0F6] text-[#E83E8C]"
                : "border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
            data-testid={`ev-filter-${s}`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 size={18} className="animate-spin text-[#E83E8C]" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-xs text-gray-400">No {tab} in this view yet.</div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filtered.map((row) => (
            <VoiceRow key={row.suggestion_id || row.concern_id} row={row} type={tab} onClick={() => setDetail({ type: tab, row })} />
          ))}
        </div>
      )}

      {detail && (
        <VoiceDetailModal
          type={detail.type}
          row={detail.row}
          authAxios={authAxios}
          onClose={() => setDetail(null)}
          onUpdated={() => { setDetail(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
const VoiceRow = ({ row, type, onClick }) => {
  const id = row.suggestion_id || row.concern_id;
  const statusStyles = {
    new: "bg-blue-50 text-blue-700",
    under_review: "bg-amber-50 text-amber-700",
    implemented: "bg-green-50 text-green-700",
    declined: "bg-gray-100 text-gray-500",
    resolved: "bg-green-50 text-green-700",
    dismissed: "bg-gray-100 text-gray-500",
  };
  return (
    <div
      onClick={onClick}
      className="border border-gray-100 rounded-xl p-3 hover:border-[#E83E8C]/40 cursor-pointer transition"
      data-testid={`voice-row-${id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-[#1E1B4B] truncate">{row.title}</p>
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${statusStyles[row.status] || statusStyles.new}`}>
              {row.status?.replace("_", " ")}
            </span>
            {type === "concerns" && row.severity && row.severity !== "normal" && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${
                row.severity === "urgent" || row.severity === "high" ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-500"
              }`}>
                {row.severity}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 flex-wrap">
            <span className="font-mono">{id}</span>
            <span className="flex items-center gap-0.5"><User size={9} /> {row.username}</span>
            {row.display_anonymous && (
              <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold">
                <ShieldAlert size={9} /> opted anonymous
              </span>
            )}
            <span className="flex items-center gap-0.5"><Clock size={9} /> {new Date(row.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
          </div>
          <p className="text-[11px] text-gray-600 mt-1 line-clamp-2">{row.details}</p>
        </div>
        <Eye size={14} className="text-gray-300 flex-shrink-0 mt-1" />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
const VoiceDetailModal = ({ type, row, authAxios, onClose, onUpdated }) => {
  const id = row.suggestion_id || row.concern_id;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const suggestActions = [
    { k: "under_review", label: "Mark Under Review", color: "bg-amber-500" },
    { k: "implemented", label: "Mark Implemented (thanks employee)", color: "bg-green-600" },
    { k: "declined", label: "Decline", color: "bg-gray-500" },
  ];
  const concernActions = [
    { k: "under_review", label: "Mark Under Review", color: "bg-amber-500" },
    { k: "resolved", label: "Mark Resolved", color: "bg-green-600" },
    { k: "dismissed", label: "Dismiss", color: "bg-gray-500" },
  ];

  const handleAction = async (status) => {
    setBusy(true); setError("");
    try {
      const endpoint =
        type === "suggestions"
          ? `${API_URL}/api/admin/doozybot/suggestions/${id}/status`
          : `${API_URL}/api/admin/doozybot/concerns/${id}/status`;
      const body =
        type === "suggestions"
          ? { status, note }
          : { status, resolution: note };
      await authAxios.post(endpoint, body);
      onUpdated();
    } catch (e) {
      setError(e?.response?.data?.detail || "Action failed");
    }
    setBusy(false);
  };

  const actions = type === "suggestions" ? suggestActions : concernActions;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="voice-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="px-5 py-3 bg-gradient-to-r from-[#1E1B4B] to-[#E83E8C] text-white flex items-start justify-between">
          <div className="min-w-0">
            <h3 className="font-bold text-sm truncate">{row.title}</h3>
            <p className="text-[10px] text-white/70 font-mono">{id} · status: {row.status}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white flex-shrink-0"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="bg-gray-50 rounded-xl p-3 space-y-1">
            <p className="text-[10px] text-gray-500 flex items-center gap-1"><User size={10} /> <b>{row.username}</b>{row.display_anonymous && <span className="ml-1 text-amber-700">(opted anonymous — keep this confidential)</span>}</p>
            <p className="text-[10px] text-gray-500">Role: {row.role} · Portal: {row.source_portal}</p>
            {type === "concerns" && <p className="text-[10px] text-gray-500">Severity: <b>{row.severity}</b></p>}
            {type === "suggestions" && <p className="text-[10px] text-gray-500">Category: <b>{row.category}</b></p>}
            <p className="text-[10px] text-gray-500 flex items-center gap-1"><Clock size={10} /> {new Date(row.created_at).toLocaleString("en-IN")}</p>
          </div>

          <div>
            <p className="text-xs font-bold text-[#1E1B4B] mb-1">Details</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{row.details}</p>
          </div>

          {(row.review_note || row.resolution) && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
              <p className="text-[10px] text-blue-700 font-bold uppercase">Previous note</p>
              <p className="text-xs text-blue-900">{row.review_note || row.resolution}</p>
            </div>
          )}

          {row.status === "implemented" && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-3 flex items-center gap-2">
              <Mail size={14} className="text-green-700" />
              <p className="text-[11px] text-green-800">Employee was thanked via Email + WhatsApp.</p>
            </div>
          )}

          {!["implemented", "resolved", "declined", "dismissed"].includes(row.status) && (
            <>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={type === "suggestions"
                  ? "Add a note — this will be included in the thank-you email/WhatsApp if you Implement"
                  : "Resolution note (optional)"}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#E83E8C]"
                data-testid="voice-action-note"
              />
              {error && <p className="text-xs text-red-500" data-testid="voice-action-error">{error}</p>}
              <div className="flex flex-wrap gap-2">
                {actions.map((a) => (
                  <button
                    key={a.k}
                    onClick={() => handleAction(a.k)}
                    disabled={busy}
                    className={`flex-1 min-w-[140px] px-3 py-2 rounded-xl text-white text-xs font-bold disabled:opacity-40 flex items-center justify-center gap-1.5 ${a.color}`}
                    data-testid={`voice-action-${a.k}`}
                  >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    {a.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeVoiceCard;
